import { Link } from "react-router-dom";
import {
  ArrowRight, Check, ChevronDown,
  Layers, ShieldCheck, FileCheck2, ClipboardList, Users, Building2, FileText, Database,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { MeyarLogo } from "@/components/MeyarLogo";
import { FaqSection } from "@/components/FaqSection";
import {
  ACCENT, DARK_BG, TINT_BG,
  FilledButton, OutlineButton, CircularProcessDiagram as SharedDiagram, AssuranceRow,
} from "@/pages/inner/meyar/_MeyarUI";

// ---------------------------------------------------------------------------
// Meyar Enterprise GRC — mirrors the Cyber Security GRC page architecture
// exactly, so the two products read as one designed family.
// ---------------------------------------------------------------------------

const FRAMEWORK_STRIP = [
  "Enterprise risk", "Controls", "Policy", "Audit", "Third-party risk", "Board reporting",
];

const FRAMEWORK_TILES = [
  { icon: Building2, label: "Oman PDPL" },
  { icon: Layers, label: "ISO 31000" },
  { icon: ShieldCheck, label: "ISO 22301" },
  { icon: FileCheck2, label: "ISO 27001" },
  { icon: Users, label: "Sector regulation" },
  { icon: FileText, label: "Internal policy" },
  { icon: ClipboardList, label: "Contractual obligations" },
  { icon: Database, label: "Your own framework" },
];

const PILLARS = [
  { n: "01", kicker: "Register", title: "See the whole picture in one register.",
    body: "Strategic, operational, financial, regulatory and reputational risk scored on the same scale, instead of argued case by case." },
  { n: "02", kicker: "Controls", title: "Prove the control, not just the intention.",
    body: "Controls linked to the risks they reduce, on a testing schedule, with the result and the evidence recorded against each test.",
    dark: true },
  { n: "03", kicker: "Policy", title: "Policies people have actually read.",
    body: "Version control, approval workflow and staff attestations with a timestamp you can produce on request.",
    dark: true },
  { n: "04", kicker: "Audit", title: "Findings that close.",
    body: "Plan audits, log findings, raise corrective actions and track each one through to verified closure." },
];

const VANTAGE_POINTS = [
  { audience: "For risk and compliance teams", headline: "Run the register, not the spreadsheet.",
    body: "One place for risk, controls, policy and evidence, with a change history behind every entry." },
  { audience: "For internal audit", headline: "Plan, test and close in the same system.",
    body: "Audit plans, findings and corrective actions linked to the risks and controls they concern.", dark: true },
  { audience: "For the board", headline: "A current position, not last quarter's.",
    body: "A live heat map, trend view and a board pack that assembles itself." },
];

const ASSURANCE_ROWS = [
  { title: "One register, many views",
    body: "Risk, controls, policy, audit and vendors reference each other, so nothing sits in isolation." },
  { title: "Decisions, not just statuses",
    body: "Every acceptance, treatment and closure carries an owner, a date and a record of who approved it." },
  { title: "Ready when asked",
    body: "Because the evidence accumulates as the work happens, producing it is a report rather than a project." },
];

const GUIDANCE_ARTICLES = [
  { n: "01", category: "Foundations",
    title: "Building a risk register your auditor accepts.",
    desc: "What good looks like — before an auditor tells you what you missed.",
    to: "/resources/blog/building-a-risk-register-your-auditor-accepts" },
  { n: "02", category: "Operations",
    title: "From finding to verified closure.",
    desc: "Building a remediation path that survives contact with a busy team.",
    to: "/resources/blog/from-finding-to-verified-closure" },
  { n: "03", category: "Programme",
    title: "Continuous assurance versus point-in-time compliance.",
    desc: "Why a programme that runs all year costs less than one that restarts every audit cycle.",
    to: "/resources/blog/continuous-assurance-vs-point-in-time-compliance" },
];

function EnterpriseDiagram() {
  return (
    <SharedDiagram
      nodes={["Identify", "Assess", "Treat", "Review"]}
      centerLine1="ALWAYS"
      centerLine2="CURRENT"
      ariaLabel="Meyar enterprise risk loop"
      testid="meyar-ent-process-diagram"
    />
  );
}

export default function EnterpriseGRC() {
  return (
    <div data-testid="meyar-enterprise-grc-page" className="bg-white">
      <Seo
        title="Meyar — Enterprise GRC"
        description="Meyar brings enterprise risk, controls, policy, audit and board reporting into one platform. Move from scattered risk to governed certainty."
      />

      {/* ============ 1. HERO (dark) ============ */}
      <section data-testid="ent-hero" style={{ backgroundColor: DARK_BG }} className="relative overflow-hidden text-white">
        <div aria-hidden="true" className="pointer-events-none absolute -end-40 -top-40 h-[520px] w-[520px] rounded-full opacity-25 blur-3xl" style={{ background: `radial-gradient(closest-side, ${ACCENT}, transparent 70%)` }} />
        <div className="relative mx-auto max-w-4xl px-4 pb-20 pt-24 text-center lg:px-6 lg:pt-32">
          <div className="mx-auto flex items-center justify-center gap-3">
            <MeyarLogo variant="knockout" className="h-6 md:h-7" />
            <span aria-hidden="true" className="h-4 w-px bg-white/30" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">Enterprise GRC</span>
          </div>
          <h1 className="mt-4 font-heading text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Move from scattered risk to{" "}
            <span className="text-wafa-sunshine">governed certainty.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/85 lg:text-lg">
            Meyar brings enterprise risk, controls, policy, audit and board reporting into one place — so the organisation knows what it carries, who owns it, and what is being done about it.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <FilledButton testid="ent-hero-primary-cta">Book a walkthrough</FilledButton>
            <OutlineButton to="/meyar" tone="dark" testid="ent-hero-secondary-cta">Explore the platform</OutlineButton>
          </div>
          <p className="mt-5 text-sm text-white/60">Built for risk, compliance and internal audit teams.</p>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-white/10 pt-6">
            {FRAMEWORK_STRIP.map((f, i) => (
              <span key={f} className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
                {f}{i < FRAMEWORK_STRIP.length - 1 ? <span className="ms-8 text-white/20">·</span> : null}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 2. STATEMENT (light, two columns) ============ */}
      <section data-testid="ent-statement" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-6">
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
                A risk register is only useful if someone owns every line.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-4">
              <p className="max-w-xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
                Most registers are written once and reviewed once a year. Meyar keeps risk live — scored consistently, owned by a named person, reviewed on a schedule, and connected to the controls that actually reduce it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 3. ACCOUNTABLE PATH (light, diagram + text) ============ */}
      <section data-testid="ent-accountable-path" style={{ backgroundColor: TINT_BG }}>
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5"><EnterpriseDiagram /></div>
            <div className="lg:col-span-7">
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl">
                Turn every risk into a decision someone made.
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
                Risk that nobody decided on is risk that nobody manages. Meyar records the treatment, the approval, the owner and the review date — so an accepted risk is a documented decision rather than an omission.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Inherent and residual scoring, so you can show a control is working",
                  "Every risk carries an owner, a review date and a treatment plan",
                  "A full change history, exportable for your auditor",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: ACCENT }}>
                      <Check className="h-3 w-3 text-white" />
                    </span>
                    <span className="text-base text-wafa-ink/85 lg:text-lg">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 4. FOUR PILLARS (tinted, 2x2, cards 2 & 3 dark) ============ */}
      <section data-testid="ent-pillars" className="bg-wafa-cream/40">
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
              Everything a governed organisation needs to run.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
              Replace scattered spreadsheets, shared drives and email threads with one connected layer your whole organisation can work from.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {PILLARS.map((p) => {
              const dark = p.dark;
              return (
                <article key={p.n} data-testid={`ent-pillar-${p.n}`}
                  className={`overflow-hidden rounded-2xl border p-8 transition-all hover:-translate-y-0.5 lg:p-10 ${dark ? "border-white/10 text-white" : "border-wafa-deep/10 bg-white text-wafa-deep"}`}
                  style={dark ? { backgroundColor: DARK_BG } : undefined}>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em]"
                      style={{ color: dark ? "#8AD48A" : ACCENT }}>
                      {p.n} · {p.kicker}
                    </div>
                    <h3 className={`mt-4 font-heading text-2xl font-semibold leading-snug sm:text-3xl ${dark ? "text-white" : "text-wafa-deep"}`}>{p.title}</h3>
                    <p className={`mt-4 max-w-md text-sm leading-relaxed lg:text-base ${dark ? "text-white/80" : "text-wafa-ink/80"}`}>{p.body}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 5. THIRD-PARTY RISK (light, single column) ============ */}
      <section data-testid="ent-third-party" className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-24 lg:px-6 lg:py-32">
          <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            The gap we find in almost every organisation.
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
            Suppliers are assessed at onboarding and rarely again. Meyar keeps vendor risk on a schedule — questionnaires sent, responses tracked, results scored, and a review date on every relationship that touches your data or your operations.
          </p>
        </div>
      </section>

      {/* ============ 6. VANTAGE POINTS (light) ============ */}
      <section data-testid="ent-vantage-points" style={{ backgroundColor: TINT_BG }}>
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
              One platform. Different points of view.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
              Meyar gives every stakeholder the detail they need, without maintaining separate versions of the truth.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {VANTAGE_POINTS.map((v, i) => {
              const dark = v.dark;
              return (
                <article key={v.audience} data-testid={`ent-vantage-${i}`}
                  className={`flex h-full flex-col rounded-2xl border p-8 transition-all hover:-translate-y-0.5 ${dark ? "border-white/10 text-white" : "border-wafa-deep/10 bg-white text-wafa-deep"}`}
                  style={dark ? { backgroundColor: DARK_BG } : undefined}>
                  <div className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: dark ? "#8AD48A" : ACCENT }}>{v.audience}</div>
                  <h3 className={`mt-4 font-heading text-2xl font-semibold leading-snug sm:text-3xl ${dark ? "text-white" : "text-wafa-deep"}`}>{v.headline}</h3>
                  <p className={`mt-5 text-sm leading-relaxed lg:text-base ${dark ? "text-white/80" : "text-wafa-ink/80"}`}>{v.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 7. FRAMEWORKS & OBLIGATIONS (dark, no button) ============ */}
      <section data-testid="ent-frameworks" style={{ backgroundColor: DARK_BG }} className="relative overflow-hidden text-white">
        <div aria-hidden="true" className="pointer-events-none absolute -start-40 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full opacity-15 blur-3xl" style={{ background: `radial-gradient(closest-side, ${ACCENT}, transparent 70%)` }} />
        <div className="relative mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Every obligation, mapped to the control that satisfies it.
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 lg:text-lg">
                Regulatory duties, contractual commitments and internal policy all place requirements on the same organisation. Map them once, and report against each without repeating the work.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {FRAMEWORK_TILES.map(({ icon: Icon, label }) => (
                  <div key={label} data-testid={`ent-framework-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex flex-col items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-white/25 hover:bg-white/[0.06]">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md border border-white/15" style={{ color: ACCENT }}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-semibold text-white/90">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 8. SOFTWARE & JUDGEMENT (light, single column) ============ */}
      <section data-testid="ent-software-judgement" className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-24 lg:px-6 lg:py-32">
          <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            Software for the record. People for the decision.
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
            A platform can hold the register, chase the review and keep the evidence. It cannot decide what level of risk your organisation is willing to carry. Meyar is built so those decisions stay with the people accountable for them, and are recorded when they are made.
          </p>
          <dl className="mt-8 grid gap-6 md:grid-cols-2">
            <div>
              <dt className="font-heading text-lg font-semibold text-wafa-deep">Guidance where the work happens</dt>
              <dd className="mt-2 text-sm text-wafa-ink/80 lg:text-base">The AI assistant drafts the control and the treatment plan beside the risk, so owners are not starting from an empty field.</dd>
            </div>
            <div>
              <dt className="font-heading text-lg font-semibold text-wafa-deep">Your advisers, inside the system</dt>
              <dd className="mt-2 text-sm text-wafa-ink/80 lg:text-base">Consultants and auditors work in the same record rather than alongside it in email.</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* ============ 9. ASSURANCE (light, accordion) ============ */}
      <section data-testid="ent-assurance" style={{ backgroundColor: TINT_BG }}>
        <div className="mx-auto max-w-5xl px-4 py-24 lg:px-6 lg:py-32">
          <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            Operate with assurance. Report with confidence.
          </h2>
          <div className="mt-10 border-t border-wafa-deep/10">
            {ASSURANCE_ROWS.map((row, i) => <AssuranceRow key={row.title} item={row} index={i} />)}
          </div>
        </div>
      </section>

      {/* ============ 10. GUIDANCE ARTICLES (tinted) ============ */}
      <section data-testid="ent-guidance-articles" className="bg-wafa-cream/40">
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
              Practical guidance for governance leaders.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
              Original, practical writing for teams building accountable risk and audit programmes.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {GUIDANCE_ARTICLES.map((a) => (
              <article key={a.n} data-testid={`ent-guidance-${a.n}`}
                className="flex h-full flex-col justify-between rounded-2xl border border-wafa-deep/10 bg-white p-7 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]">
                <div>
                  <div className="flex items-baseline gap-3">
                    <span className="font-heading text-3xl font-semibold" style={{ color: ACCENT }}>{a.n}</span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-wafa-ink/60">{a.category}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold leading-snug text-wafa-deep sm:text-2xl">{a.title}</h3>
                  <p className="mt-4 text-sm text-wafa-ink/75 lg:text-base">{a.desc}</p>
                </div>
                <div className="mt-8">
                  <Link to={a.to} data-testid={`ent-guidance-read-${a.n}`}
                    className="inline-flex items-center gap-2 rounded-md border border-wafa-primary px-4 py-2 text-sm font-semibold text-wafa-primary transition-colors hover:bg-wafa-primary hover:text-white">
                    Read <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ (preserved from original) ============ */}
      <FaqSection heading="Common questions" items={[
        { q: "How does it integrate with our existing tools?",
          a: "Meyar reads from and writes to your existing systems — HR, IT, finance, ticketing — via APIs and scheduled imports. Evidence stays where it lives; Meyar keeps the record of what applied when." },
        { q: "Can we start with a single risk framework and add more?",
          a: "Yes. Frameworks are configurable and can be added later without rebuilding the register." },
        { q: "How is it different from Meyar Cyber Security GRC?",
          a: "Enterprise GRC covers the whole organisation. Cyber Security GRC covers the information-security control set specifically. Same platform, same evidence store — you can run one or both." },
      ]} />

      {/* ============ 11. CLOSING CTA (dark) ============ */}
      <section data-testid="ent-closing-cta" style={{ backgroundColor: DARK_BG }} className="relative overflow-hidden text-white">
        <div aria-hidden="true" className="pointer-events-none absolute -end-32 bottom-0 h-[420px] w-[420px] rounded-full opacity-20 blur-3xl" style={{ background: `radial-gradient(closest-side, ${ACCENT}, transparent 70%)` }} />
        <div className="relative mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <h2 className="font-heading text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Know what you carry.
                <br />
                <span className="text-wafa-sunshine">Prove you manage it.</span>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 lg:text-lg">
                Bring enterprise risk, controls, policy and audit into one accountable operating system.
              </p>
              <div className="mt-8">
                <FilledButton testid="ent-closing-cta-btn">Book a walkthrough</FilledButton>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                <div className="font-heading text-lg font-semibold text-white">
                  See Meyar with your own register loaded
                </div>
                <ul className="mt-6 space-y-4">
                  {[
                    "Import your existing risk register from Excel",
                    "Map your controls to the obligations you actually carry",
                    "Walk through a board report built from your own data",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-3">
                      <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: ACCENT }}>
                        <Check className="h-3 w-3 text-white" />
                      </span>
                      <span className="text-sm text-white/85 lg:text-base">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
